/**
 * Browser milliseconds as something a person reads - pure, and tested.
 *
 * Its own file rather than a helper inside the panel, for the same reason
 * `calls/cost/money.ts` is one: these are decisions about a figure somebody
 * budgets against, and a decision that lives inside a component is a decision
 * nobody can test.
 *
 * WHY NOT `crm/ui/format.ts#fmtDuration`. That one renders a call as `mm:ss`,
 * which is right for a phone call and wrong here: browser time spans a 900 ms
 * capture at one end and ten hours of monthly allowance at the other, and
 * `00:00` for the capture would say a screenshot was free. The money half is
 * NOT duplicated - `fmtMicros` in `calls/cost/money.ts` already does it, and the
 * panel imports that file directly rather than growing a second one.
 */

/** A minute and an hour, so the thresholds below read as what they are. */
const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * "912 ms" / "4.3 s" / "9 min 41 s" / "2 h 14 min".
 *
 * The precision follows the size, exactly as the money formatter's decimals do.
 * Seconds survive under the hour because the free plan's whole allowance is ten
 * minutes: rounding "9 min 41 s" to "9 min" hides the part of the day that is
 * actually running out. Above an hour they stop mattering and would be noise on
 * a figure being compared with a monthly allowance.
 */
export function fmtBrowserMs(ms: number): string {
  const total = Number.isFinite(ms) ? Math.max(0, ms) : 0;
  if (total < 1000) return `${Math.round(total)} ms`;
  if (total < MINUTE_MS) return `${(total / 1000).toFixed(1)} s`;
  if (total < HOUR_MS) {
    const minutes = Math.floor(total / MINUTE_MS);
    const seconds = Math.round((total % MINUTE_MS) / 1000);
    // 59.7 s rounds to 60 and would print "9 min 60 s". Carry it.
    if (seconds === 60) return `${minutes + 1} min`;
    return seconds > 0 ? `${minutes} min ${seconds} s` : `${minutes} min`;
  }
  const hours = Math.floor(total / HOUR_MS);
  const minutes = Math.round((total % HOUR_MS) / MINUTE_MS);
  if (minutes === 60) return `${hours + 1} h`;
  return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
}

/**
 * How wide the day's meter is drawn, as a percentage.
 *
 * Two rules that look cosmetic and are not. Some usage always gets a visible
 * sliver, because a bar that disappears reads as "nothing ran today" and that is
 * a different claim from "a little ran". And the bar stops at 100 rather than
 * overflowing its track: past the wall the interesting fact is that the wall was
 * reached, which the accompanying line says in words.
 */
export function meterPct(usedMs: number, allowanceMs: number): number {
  if (!Number.isFinite(usedMs) || !Number.isFinite(allowanceMs) || allowanceMs <= 0) return 0;
  const used = Math.max(0, usedMs);
  if (used === 0) return 0;
  return Math.min(100, Math.max(1.5, (used / allowanceMs) * 100));
}
